"use client";

import React, { useCallback, useMemo } from "react";
import {
  Flex,
  Box,
  Icon,
  Text,
  buildPath,
  getCustomDomainComponents,
  buildURL,
  SpacePermission,
  useNavigation,
} from "@thenounproject/lingo-core";
import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";

import { NavPointTypes } from "@redux/navPoint/NavPoint";
import { useSelectSpace } from "@selectors/entities/spaces";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import { useGetUser } from "@selectors/getters";
import useSpaces from "@redux/actions/spaces/useSpaces";
import useNavPoint from "@hooks/useNavPoint";

export const SUPER_NAV_HEIGHT = "65px";

// type Props = {
//   navPoint: NavPoint;
// };

const buttonProps = {
  style: {
    padding: "4px",
    height: "36px",
    width: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

const MobileSuperNav = () => {
  const navPoint = useNavPoint();
  const space = useSelectSpace();
  const user = useGetUser();
  const navigate = useNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { showModal, dismissModal } = useShowModal();

  React.useEffect(() => {
    dismissModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  const { data: spaces = [] } = useSpaces(null, { skip: !user?.id });

  const buildLinkMenuItem = useCallback(
    (to: string, title: string, iconId?: string, avatar?: string) => {
      return {
        type: "Item" as const,
        itemProps: {
          title,
          image: {
            iconId: iconId ?? "navigation.kit",
            src: avatar ?? undefined,
          },
          onClick: () => {
            navigate.push(to);
          },
        },
      };
    },
    [navigate]
  );

  const buildDialogItem = (text: string) => {
    return {
      type: "Dialog" as const,
      dialogProps: { text, background: "grayLightest", borderTop: "default" },
    };
  };

  const iconProps = {
    size: 24,
    fill: "white",
  };

  const shouldHideSearchButton = useMemo(() => {
    if (navPoint?.type === NavPointTypes.UserSettings) return true;
    return false;
  }, [navPoint?.type]);

  const shouldHideMenuButton = useMemo(() => {
    return !space?.access?.role;
  }, [space]);

  const mainMenuItems = useMemo(() => {
    switch (navPoint?.type) {
      case NavPointTypes.UserSettings:
        return [
          buildLinkMenuItem("/settings/profile", "Profile", "navigation.user"),
          buildLinkMenuItem("/settings/password", "Password", "info.lock-closed"),
          buildLinkMenuItem("/settings/email", "Email", "navigation.mail"),
          buildLinkMenuItem("/settings/privacy", "Privacy", "navigation.privacy"),
        ];
      default: {
        const defaultMenuItems = [];
        const permissions = space?.access?.permissions ?? [];
        const canViewDashboard = permissions.includes(SpacePermission.viewDashboard);
        const canViewLibrary = permissions.includes(SpacePermission.viewAssetLibrary);
        const canViewMembers = permissions.includes(SpacePermission.viewMembers);
        const canViewInsights = permissions.includes(SpacePermission.viewAnalytics);
        const canViewSettings = permissions.includes(SpacePermission.edit);
        const canViewBilling = permissions.includes(SpacePermission.manageBilling);

        if (canViewDashboard) {
          defaultMenuItems.push(
            buildLinkMenuItem(
              buildPath("/dashboard", { space }),
              "Dashboard",
              "navigation.kit-menu"
            )
          );
        }
        if (canViewLibrary) {
          defaultMenuItems.push(
            buildLinkMenuItem(buildPath("/library", { space }), "Library", "marketing.storage")
          );
        }
        if (canViewMembers) {
          defaultMenuItems.push(
            buildLinkMenuItem(buildPath("/members", { space }), "Users", "navigation.user-group")
          );
        }

        if (canViewInsights) {
          defaultMenuItems.push(
            buildLinkMenuItem(buildPath("/insights", { space }), "Insights", "navigation.chart")
          );
        }
        if (canViewSettings) {
          defaultMenuItems.push(
            buildLinkMenuItem(buildPath("/settings", { space }), "Settings", "navigation.settings")
          );
        }
        if (canViewBilling) {
          defaultMenuItems.push(
            buildLinkMenuItem(buildPath("/billing", { space }), "Billing", "navigation.credit-card")
          );
        }
        return defaultMenuItems;
      }
    }
  }, [buildLinkMenuItem, navPoint?.type, space]);

  const userMenuItems = useMemo(() => {
    const domain = getCustomDomainComponents();
    const spaceMenuItems =
      spaces
        .filter(space => space.access.isPublic === false && space.status === "active")
        .map(space => {
          const newURL = domain.subdomain !== space.subdomain || domain.host !== space.domain;
          return {
            type: "Item" as const,
            itemProps: {
              title: space.name,
              image: { iconId: "content.space", src: space.avatar, imageStyle: "circle" as const },
              link: newURL ? buildURL("/dashboard", { space }) : buildPath("/", { space }),
            },
          };
        }) ?? [];

    return [
      buildDialogItem("Spaces"),
      ...spaceMenuItems,
      buildDialogItem("Other"),
      {
        type: "Item" as const,
        itemProps: {
          title: "Account settings",
          image: { iconId: "navigation.user" },
          onClick: () => {
            navigate.push("/settings/profile");
            dismissModal();
          },
        },
      },
      {
        type: "Item" as const,
        itemProps: {
          title: "Log out",
          image: { iconId: "action.logout" },
          onClick: () => {
            navigate.push("/logout");
            dismissModal();
          },
        },
      },
    ];
  }, [dismissModal, navigate, spaces]);

  const onClickUserMenuItem = useCallback(() => {
    showModal(ModalTypes.MENU_MODAL, { menuItems: userMenuItems });
  }, [showModal, userMenuItems]);

  const onClickSearchMenuItem = useCallback(() => {
    showModal(ModalTypes.SEARCH_MODAL, {});
  }, [showModal]);

  const onClickMainMenuItem = useCallback(() => {
    showModal(ModalTypes.MENU_MODAL, { menuItems: mainMenuItems });
  }, [mainMenuItems, showModal]);
  return (
    <Flex
      as="nav"
      position="fixed"
      top={0}
      left={0}
      width="100%"
      zIndex={999}
      height={SUPER_NAV_HEIGHT}
      px="16px"
      flexDirection="column"
      justifyContent="center"
      borderBottom="1px solid black"
      background="black">
      <Flex width="100%" alignItems="center">
        <Box flex="1">
          {user?.id ? (
            <button onClick={onClickUserMenuItem} {...buttonProps}>
              <Icon iconId="navigation.user" {...iconProps} />
            </button>
          ) : (
            <Link href="/space">
              <Text color="white">Login</Text>
            </Link>
          )}
        </Box>
        <Flex height={48} flex="1" justifyContent="center" alignItems="center">
          <Icon iconId="logo.mark" fill="white" size={48} />
        </Flex>
        <Flex flex="1" gap="16px" justifyContent="flex-end">
          {!shouldHideSearchButton && (
            <button onClick={onClickSearchMenuItem} {...buttonProps}>
              <Icon iconId="navigation.search" {...iconProps} />
            </button>
          )}
          {!shouldHideMenuButton && (
            <button onClick={onClickMainMenuItem} {...buttonProps}>
              <Icon iconId="navigation.menu" {...iconProps} />
            </button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default React.memo(MobileSuperNav);
